import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@shared/components/ui/chart';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import { ComponentType } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  CartesianGridProps,
  LabelList,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { DataKey, Margin } from 'recharts/types/util/types';
import { CustomTooltipProps } from './tooltips/types';

interface BarChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  chartConfig: ChartConfig;
  chartData: any;
  legend?: boolean;
  className?: ClassValue;
  layout?: 'horizontal' | 'vertical';
  margin?: Margin;
  YAxisProps?: YAxisProps;
  XAxisProps?: XAxisProps;
  MaxBarSize?: number;
  barCategoryGap?: number | string;
  barSize?: number;
  stacked?: boolean;
  /** Renders each bar's value inside the top of the bar (zeroes hidden). */
  showBarLabels?: boolean;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
  tooltipLabelFormatter?: (
    label: string,
    payload: Array<{ payload?: Record<string, unknown> }>
  ) => React.ReactNode;
  tooltipValueFormatter?: (value: number | string) => React.ReactNode;
  CartesianGridProps?: Partial<CartesianGridProps>;
  topRadius?: number;
}

export default function SimpleBarChart<
  T extends DataKey<string>,
  U extends DataKey<string[]>,
>({
  nameKey,
  dataKey,
  chartData,
  chartConfig,
  legend,
  className,
  layout = 'horizontal',
  margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  YAxisProps,
  XAxisProps,
  MaxBarSize = 100,
  barCategoryGap = '10%',
  barSize,
  tooltipFormatter: Component,
  tooltipLabelFormatter,
  tooltipValueFormatter,
  stacked = true,
  showBarLabels = false,
  CartesianGridProps: cartesianGridProps,
  topRadius = 0,
}: BarChartProps<T, U>) {
  return (
    <ChartContainer config={chartConfig} className={cn('', className)}>
      <BarChart
        accessibilityLayer
        data={chartData}
        layout={layout}
        margin={margin}
        barCategoryGap={barCategoryGap}
      >
        <CartesianGrid
          vertical={false}
          stroke="var(--border)"
          {...cartesianGridProps}
        />
        {layout === 'horizontal' && (
          <>
            <XAxis
              dataKey={nameKey}
              tickLine={false}
              tickMargin={10}
              type="category"
              {...XAxisProps}
            />
            <YAxis
              tickLine={false}
              tickMargin={10}
              tickFormatter={(value) => value}
              {...YAxisProps}
            />
          </>
        )}

        {layout === 'vertical' && (
          <>
            <XAxis
              type="number"
              dataKey={dataKey}
              orientation="top"
              {...XAxisProps}
            />
            <YAxis
              dataKey={nameKey}
              type="category"
              tickLine={false}
              tickMargin={10}
              {...YAxisProps}
            />
          </>
        )}
        <ChartTooltip
          content={
            <ChartTooltipContent
              className={cn('w-60', {
                'w-fit': Component,
              })}
              labelFormatter={tooltipLabelFormatter}
              valueFormatter={tooltipValueFormatter}
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        {legend && (
          <ChartLegend
            content={<ChartLegendContent className="text-md" />}
            verticalAlign="top"
          />
        )}
        {Object.keys(chartConfig).map((key, index, keys) => {
          const isTop = !stacked || index === keys.length - 1;
          const radius: [number, number, number, number] =
            isTop && topRadius > 0
              ? [topRadius, topRadius, 0, 0]
              : [0, 0, 0, 0];
          return (
            <Bar
              dataKey={key}
              key={key}
              stackId={stacked ? 'a' : undefined}
              fill={chartConfig[key].color}
              radius={radius}
              maxBarSize={MaxBarSize}
              isAnimationActive={false}
              barSize={barSize}
            >
              {showBarLabels && (
                <LabelList
                  dataKey={key}
                  position="insideTop"
                  offset={8}
                  fill="#fff"
                  fontSize={12}
                  fontWeight={600}
                  formatter={(value: number) => (value ? value : '')}
                />
              )}
            </Bar>
          );
        })}
      </BarChart>
    </ChartContainer>
  );
}
